import { createApp, defineAsyncComponent } from "vue";
import { i18n } from "@/i18n.js";
import { createAnalytics } from "@/plugins/analytics";
import { useGlobalStore } from "@/stores/global-pinia.js";
import { qModEvent } from "@/utils/eventemitter";
import { createPinia } from "pinia";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import { qmTheme } from "@/assets/qmTheme";
import Tooltip from "primevue/tooltip";
import { primevueLocales } from "@/config/primevue-locales";

import { QModPerformanceMetrics } from "@/plugins/metrics";
import componentMetadata from "virtual:component-metadata";

/* global __QMODII_VERSION__ */

// Full catalog kept private — only init'd tools are exposed on meta.tools
const _allComponentMetadata = componentMetadata;

async function loadLocale(locale) {
    const module = await import(`@/locales/${locale}.json`);
    return module.default ?? module;
}

const App = defineAsyncComponent(() => import("./QModII.vue"));

const locales = ["en", "fr"];

const sharedPinia = createPinia();

export async function init(
    qmWMID,
    qmComponentDOMElement,
    qmComponentName,
    qmComponentProps,
    qmLanguage = "en",
    qmEnv = "app",
    qmSID = null,
    qmExtra = {}
) {
    const messages = await loadLocale(qmLanguage);
    i18n.global.setLocaleMessage(qmLanguage, messages);
    i18n.global.locale.value = qmLanguage;

    const Analytics = createAnalytics(qmExtra.Analytics ? qmExtra.Analytics : {});

    // Create Pinia instance first
    const qModII = createApp(App, {
        qmWMID: qmWMID,
        qmDOMid: qmComponentDOMElement.replace("#", ""),
        qmComponentName: qmComponentName,
        qmComponentProps: qmComponentProps,
        qmEnv,
        qmSID,
    });

    qModII.use(sharedPinia);

    // Initialize Pinia global store (must be after use(sharedPinia))
    // Update runtime env and register this tool's metadata
    if (window.qModII?.meta) {
        window.qModII.meta.currentEnv = qmEnv;

        // Register the initialized tool's metadata from the full catalog
        const toolKey = qmComponentName.toLowerCase();
        if (_allComponentMetadata[toolKey] && !window.qModII.meta.tools[toolKey]) {
            window.qModII.meta.tools[toolKey] = Object.freeze(_allComponentMetadata[toolKey]);
        }
    }

    const globalStore = useGlobalStore();
    globalStore.initialize({
        wmid: qmWMID,
        env: qmEnv,
        sid: qmSID,
        lang: qmLanguage,
        metrics: new QModPerformanceMetrics(),
    });

    globalStore.setDefaultSelection({
        portfolioId: qmExtra?.defaultPortfolioId ?? qmExtra?.defaultSelection?.portfolioId ?? null,
        tradeAccountId:
            qmExtra?.defaultTradeAccountId ?? qmExtra?.defaultSelection?.tradeAccountId ?? null,
    });

    qModII.directive("voiceoverFocus", (el, binding) => {
        var focusInterval = 10; // ms, time between function calls
        var focusTotalRepetitions = 10; // number of repetitions

        el.setAttribute("tabindex", "0");
        el.blur();

        var focusRepetitions = 0;
        var interval = window.setInterval(function () {
            el.focus();
            focusRepetitions++;
            if (focusRepetitions >= focusTotalRepetitions) {
                window.clearInterval(interval);
            }
        }, focusInterval);
    });

    qModII.use(i18n);
    qModII.use(Analytics);

    // Use PrimeVue with the custom theme and locale
    console.log(qmLanguage)
    qModII.use(ToastService);
    qModII.use(PrimeVue, {
        // unstyled: true,
        theme: {
            preset: qmTheme,
            options: {
                prefix: "qmodii",
                darkModeSelector: ".qmodii-theme-dark",
                cssLayer: false,
            },
        },
        locale: primevueLocales[qmLanguage] || primevueLocales.en,
    });

    qModII.directive("tooltip", Tooltip);

    qModII.mount(qmComponentDOMElement);
}

export function changeLang(lang) {
    qModII.$i18n.locale = lang;
    // Update PrimeVue locale when language changes
    if (qModII.config.globalProperties.$primevue) {
        qModII.config.globalProperties.$primevue.config.locale =
            primevueLocales[lang] || primevueLocales.en;
    }
}

export function setTheme(theme) {
    qModTheme = theme;
}

if (document.readyState != "loading") {
    document.addEventListener(
        "DOMContentLoaded",
        () => {
            qModEvent({
                type: "LOADER",
                event: "FINISHED",
            });
        },
        false
    );
} else {
    qModEvent({
        type: "LOADER",
        event: "FINISHED",
    });
}

window.qModII = window.qModII || {};
window.qModII.init = init;
window.qModII.changeLang = changeLang;
window.qModII.setTheme = setTheme;

// Metadata — separate from api (which holds per-tool runtime methods)
window.qModII.meta = {
    currentVersion: __QMODII_VERSION__,
    currentEnv: null,
    tools: {},
    // Full catalog for programmatic discovery without loading tools
    allTools: Object.freeze(_allComponentMetadata),
};
